import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Snackbar, Alert, Slide } from "@mui/material";
import { setNotificationHandlers, clearNotificationHandlers } from "./notifyBridge";

const NotificationContext = createContext(null);

function SlideDown(props) {
  return <Slide {...props} direction="down" />;
}

export function NotificationProvider({ children }) {
  const [queue, setQueue] = useState([]);

  const remove = useCallback((id) => {
    setQueue((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback((severity, message, opts = {}) => {
    const id = opts.id ?? `${Date.now()}-${Math.random()}`;
    setQueue((prev) => [...prev, { id, severity, message, duration: opts.duration ?? 4000 }]);
    return id;
  }, []);

  const value = useMemo(
    () => ({
      notifySuccess: (msg, opts) => notify("success", msg, opts),
      notifyError: (msg, opts) => notify("error", msg, opts),
      notifyInfo: (msg, opts) => notify("info", msg, opts),
      notifyWarning: (msg, opts) => notify("warning", msg, opts),
    }),
    [notify]
  );

  // Register with the module-level notifyBridge so non-React code (e.g. the
  // axios response interceptor) can fire toasts via notify.error()/etc.
  useEffect(() => {
    setNotificationHandlers({
      success: (msg, opts) => notify("success", msg, opts),
      error:   (msg, opts) => notify("error",   msg, opts),
      info:    (msg, opts) => notify("info",    msg, opts),
      warning: (msg, opts) => notify("warning", msg, opts),
    });
    return () => {
      clearNotificationHandlers();
    };
  }, [notify]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {queue.map((item) => (
        <Snackbar
          key={item.id}
          open
          autoHideDuration={item.duration}
          onClose={() => remove(item.id)}
          TransitionComponent={SlideDown}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{ mt: 2 }}
        >
          <Alert onClose={() => remove(item.id)} severity={item.severity} variant="filled">
            {item.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
}
